import axios from 'axios'
import { instance } from './api'

export type GuestPerson = { id: number | null; authorName: string; avatar?: string; isGuest?: boolean }
export type GuestChat = {
  isClosed?: boolean
  id: number
  name: string
  competence: number
  competenceName: string
  expert: GuestPerson
  unreadCount: number
  lastMessage: string
}
export type GuestMessage = {
  id: number
  user: GuestPerson | null
  message: string
  created: string
  isPinned: boolean
  isEdited: boolean
  parent?: { id: number; user: GuestPerson | null; message: string; isDeleted: boolean } | null
}
export type GuestWindow = {
  results: GuestMessage[]
  pinned: GuestMessage[]
  olderCursor: string | null
  hasOlder: boolean
}
export type GuestChoice = { id: number; name: string; experts: GuestPerson[] }
export type GuestStart = {
  name: string
  competence: number
  expert: number
  consent: boolean
  message: string
  clientId: string
  captchaKey?: string
  captchaValue?: string
}

const client = axios.create({ baseURL: process.env.REACT_APP_ENDPOINT, withCredentials: true })
const base = '/api/forum/guest'
let csrf = ''
let recoveryToken = ''
let recoveryListenerInstalled = false

export const captureGuestRecovery = () => {
  if (!recoveryListenerInstalled) {
    recoveryListenerInstalled = true
    window.addEventListener('hashchange', () => {
      captureGuestRecovery()
      if (recoveryToken) window.dispatchEvent(new CustomEvent('guest-chat.recover'))
    })
  }
  const params = new URLSearchParams(window.location.hash.slice(1))
  const token = params.get('guestChat')
  if (token) {
    recoveryToken = token
    params.delete('guestChat')
    window.history.replaceState(
      window.history.state,
      '',
      window.location.pathname + window.location.search + (params.toString() ? `#${params.toString()}` : ''),
    )
  }
}
const config = () => ({ headers: { 'X-CSRFToken': csrf }, withCredentials: true })
const post = async <T>(path: string, data = {}) => (await client.post<T>(`${base}/${path}/`, data, config())).data

export const guestChatAPI = {
  session: async () => {
    const data = (await client.get<{ csrfToken: string; chats: GuestChat[] }>(`${base}/session/`)).data
    csrf = data.csrfToken
    return data.chats
  },
  choices: async () => (await client.get<GuestChoice[]>(`${base}/choices/`)).data,
  start: (data: GuestStart) => post<GuestChat>('chats', data),
  messages: async (id: number, before?: string) =>
    (await client.get<GuestWindow>(`${base}/chats/${id}/messages/`, { params: before ? { before } : undefined })).data,
  send: (id: number, message: string, clientId: string) =>
    post<GuestMessage>(`chats/${id}/messages`, { message, clientId }),
  read: (id: number, commentId: number) => post(`chats/${id}/read`, { commentId }),
  link: (id: number) => post<{ token: string }>(`chats/${id}/recovery`),
  recover: async () => {
    if (!recoveryToken) return undefined
    const token = recoveryToken
    const chat = await post<GuestChat>('recover', { token })
    if (recoveryToken === token) recoveryToken = ''
    return chat
  },
  ticket: () => post<{ ticket: string }>('ticket'),
  claimable: async () => (await instance.get<GuestChat[]>(`${base}/claim/`, config())).data,
  claim: async () => (await instance.post(`${base}/claim/`, {}, config())).data,
}

export const openGuestChat = (competence: number, expert: number) =>
  window.dispatchEvent(new CustomEvent('guest-chat.open', { detail: { competence, expert } }))
